import React from 'react';

import { RecipeSection } from '@ledium/types';
import { CircadianChart, invertColor, sectionBgColor } from '@ledium/ui-common';
import { Box, Paper, Popper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

interface SectionSegmentProps {
    section: RecipeSection;
    widthPercent: number;
}

function formatDuration(totalHours: number): string {
    if (totalHours % 24 === 0) {
        return `${totalHours / 24}d`;
    }
    return `${totalHours}h`;
}

const SectionSegment = ({ section, widthPercent }: SectionSegmentProps) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

    const bgColor = sectionBgColor(section.sectionColor?.background, theme.palette.mode === 'dark') ?? '#888888';
    const totalHours = section.repeatNum * section.circadianLength;

    const chartData = section.circadianEvents.map((e) => ({
        time: e.hour + e.minute / 60,
        value: e.sumLightsIntensity ?? 0
    }));

    return (
        <>
            <Box
                data-testid="section-segment"
                onMouseEnter={(e) => setAnchorEl(e.currentTarget)}
                onMouseLeave={() => setAnchorEl(null)}
                sx={{
                    flex: widthPercent,
                    minWidth: 4,
                    bgcolor: bgColor,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 0.5,
                    cursor: 'default'
                }}
            />
            <Popper
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                placement="bottom"
                modifiers={[{ name: 'flip', enabled: true }]}
                style={{ pointerEvents: 'none', zIndex: 1300 }}
            >
                <Paper elevation={8} sx={{ p: 2, width: 300, pointerEvents: 'none' }}>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 'bold',
                            bgcolor: bgColor,
                            color: invertColor(bgColor),
                            px: 1,
                            borderRadius: 0.5,
                            border: '1px solid',
                            borderColor: 'divider',
                            display: 'inline-block',
                            mb: 0.5
                        }}
                    >
                        {section.name}
                    </Typography>
                    <Typography variant="body2" display="block" color="text.secondary" sx={{ mb: 1, pl: 1 }}>
                        {section.repeatNum} &times; {section.circadianLength}h &mdash; {formatDuration(totalHours)}
                    </Typography>
                    <CircadianChart data={chartData} mini />
                </Paper>
            </Popper>
        </>
    );
};

export default React.memo(SectionSegment);
